import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1280px] mx-auto px-20 py-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/da365679-439e-4e05-8b35-80575828d485.png" alt="Moswines AS Logo" className="h-10 w-10 object-contain" />
            <h1 className="text-2xl font-semibold text-[#2B2B2B]">Moswines AS</h1>
          </div>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className="text-sm text-[#6B6B6B] hover:text-[#C17B5A] transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-sm text-[#6B6B6B] hover:text-[#C17B5A] transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-sm text-[#6B6B6B] hover:text-[#C17B5A] transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-sm text-[#6B6B6B] hover:text-[#C17B5A] transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm text-[#6B6B6B] hover:text-[#C17B5A] transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-24 pb-40 px-20">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFF5ED] to-[#FDFBF7]"></div>
        <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-[0.15]">
          <img src="https://public.readdy.ai/ai/img_res/095dfa0944a0dcccd665c45e3e79f78e.jpg" alt="" className="w-full h-full object-cover object-center" />
        </div>
        
        <div className="max-w-[1280px] mx-auto relative z-10 w-full">
          <div className="w-[45%]">
            <h2 className="text-[72px] leading-[1.2] text-[#1A1A1A] font-serif mb-8">
              <span className="font-normal">Modern iOS Solutions</span><br />
              <span className="italic font-normal">for Enhanced Security</span>
            </h2>
            <p className="text-lg text-[#5A5A5A] leading-[1.8] max-w-[480px] font-light">
              Moswines AS is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
          
          <div className="absolute bottom-20 left-0">
            <span className="text-[11px] uppercase tracking-wider text-[#A67C6D]">/iOS Development</span>
          </div>
          
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
            <div className="flex gap-2">
              <div className="w-14 h-14 rounded-full overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=professional%20developer%20portrait%20monochrome%20sepia%20tone%20filter%20business%20headshot%20clean%20background%20minimalist%20photography&width=120&height=120&seq=team-001&orientation=squarish" alt="Team member" className="w-full h-full object-cover" />
              </div>
              <div className="w-14 h-14 rounded-full overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=professional%20software%20engineer%20portrait%20monochrome%20sepia%20tone%20filter%20business%20headshot%20clean%20background%20minimalist%20photography&width=120&height=120&seq=team-002&orientation=squarish" alt="Team member" className="w-full h-full object-cover" />
              </div>
              <div className="w-14 h-14 rounded-full overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=professional%20iOS%20developer%20portrait%20monochrome%20sepia%20tone%20filter%20business%20headshot%20clean%20background%20minimalist%20photography&width=120&height=120&seq=team-003&orientation=squarish" alt="Team member" className="w-full h-full object-cover" />
              </div>
              <div className="w-14 h-14 rounded-full overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=professional%20tech%20specialist%20portrait%20monochrome%20sepia%20tone%20filter%20business%20headshot%20clean%20background%20minimalist%20photography&width=120&height=120&seq=team-004&orientation=squarish" alt="Team member" className="w-full h-full object-cover" />
              </div>
            </div>
            <span className="text-sm text-[#5A5A5A] ml-2 whitespace-nowrap">Expert Team of 12+ Developers</span>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white border-t border-[#E8E8E8] py-40 px-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-20">
            <h3 className="text-5xl font-serif text-[#3D3D3D] mb-6">About Moswines AS</h3>
            <div className="w-20 h-[1px] bg-[#C17B5A] mx-auto"></div>
          </div>
          
          <div className="flex gap-[5%]">
            <div className="w-[55%]">
              <p className="text-base text-[#4A4A4A] leading-[1.9] mb-6">
                At Moswines AS, we are dedicated to creating innovative iOS applications that prioritize user security and device performance. Our team of experienced developers combines technical expertise with a deep understanding of mobile user needs to deliver exceptional products.
              </p>
              <p className="text-base text-[#4A4A4A] leading-[1.9] mb-6">
                Our flagship products include a robust VPN solution that ensures secure and private internet browsing, and a comprehensive Cleaner application that optimizes device performance by removing unnecessary files and managing storage efficiently. Each product is meticulously crafted to provide seamless integration with the iOS ecosystem.
              </p>
              <p className="text-base text-[#4A4A4A] leading-[1.9]">
                We believe in continuous innovation and maintaining the highest standards of quality. Our commitment to the iOS platform allows us to leverage the latest technologies and design principles, ensuring our applications remain at the forefront of mobile security and performance optimization.
              </p>
            </div>
            
            <div className="w-[40%]">
              <div className="bg-[#FAF7F2] rounded-3xl p-12 h-full flex flex-col justify-center gap-12">
                <div className="text-center">
                  <div className="text-[32px] font-bold text-[#2B2B2B] mb-2">2</div>
                  <div className="text-sm text-[#6B6B6B]">Core Products</div>
                </div>
                <div className="text-center">
                  <div className="text-[32px] font-bold text-[#2B2B2B] mb-2">iOS</div>
                  <div className="text-sm text-[#6B6B6B]">Platform Focus</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#FDFBF7] py-40 px-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex justify-between items-start mb-24">
            <h3 className="text-[56px] font-serif font-extrabold text-black leading-tight max-w-[500px]">Our Products</h3>
            <p className="text-base text-[#7A7A7A] max-w-[380px] text-right leading-relaxed">
              We develop powerful iOS applications that combine security, performance, and user-friendly design to deliver exceptional mobile experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-[2%]" data-product-shop>
            {/* VPN Product Card */}
            <div className="bg-white rounded-[20px] border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-[280px] bg-white flex items-center justify-center p-8">
                <img src="https://readdy.ai/api/search-image?query=VPN%20shield%20icon%20gradient%20blue%20modern%20minimalist%20design%20security%20protection%20symbol%20clean%20simple%20illustration%20abstract%20geometric%20shape%20professional%20tech%20graphic%20light%20background&width=400&height=400&seq=vpn-product-001&orientation=squarish" alt="VPN Solution" className="w-full h-full object-contain" />
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-serif text-[#2B2B2B] mb-3">VPN Solution</h4>
                <p className="text-sm text-[#6B6B6B] leading-[1.6]">
                  Secure your online presence with our advanced VPN application. Enjoy encrypted connections, anonymous browsing, and access to global content while protecting your privacy on any network.
                </p>
              </div>
            </div>
            
            {/* Cleaner Product Card */}
            <div className="bg-white rounded-[20px] border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-[280px] bg-white flex items-center justify-center p-8">
                <img src="https://readdy.ai/api/search-image?query=cleaning%20brush%20icon%20gradient%20orange%20modern%20minimalist%20design%20optimization%20symbol%20clean%20simple%20illustration%20abstract%20geometric%20shape%20professional%20tech%20graphic%20light%20background&width=400&height=400&seq=cleaner-product-001&orientation=squarish" alt="Cleaner Application" className="w-full h-full object-contain" />
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-serif text-[#2B2B2B] mb-3">Cleaner Application</h4>
                <p className="text-sm text-[#6B6B6B] leading-[1.6]">
                  Optimize your device performance with our intelligent Cleaner app. Remove junk files, manage storage efficiently, and keep your iOS device running smoothly with automated maintenance features.
                </p>
              </div>
            </div>
            
            {/* Coming Soon Card */}
            <div className="bg-white rounded-[20px] border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-[280px] bg-white flex items-center justify-center p-8">
                <img src="https://readdy.ai/api/search-image?query=abstract%20placeholder%20icon%20gradient%20grayscale%20modern%20minimalist%20design%20question%20mark%20symbol%20clean%20simple%20illustration%20geometric%20shape%20professional%20tech%20graphic%20light%20background&width=400&height=400&seq=coming-soon-001&orientation=squarish" alt="Coming Soon" className="w-full h-full object-contain" />
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-serif text-[#2B2B2B] mb-3">Coming Soon</h4>
                <p className="text-sm text-[#6B6B6B] leading-[1.6]">
                  We are constantly innovating and developing new solutions for iOS users. Stay tuned for our upcoming products that will further enhance your mobile experience and security.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-40 px-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex justify-between items-start mb-24">
            <h3 className="text-[64px] font-serif font-bold text-black leading-tight max-w-[500px]">Key Features</h3>
            <p className="text-base text-[#6A6A6A] max-w-[420px] text-right leading-relaxed">
              Our applications are built with cutting-edge technology and designed to provide the best possible experience for iOS users, focusing on security, privacy, and performance.
            </p>
          </div>
          
          <div className="flex gap-[4%]">
            {/* Feature 1 */}
            <div className="w-[40%] bg-[#F8F8F8] rounded-2xl p-12">
              <div className="w-full h-[120px] flex items-center justify-center mb-8">
                <img src="https://readdy.ai/api/search-image?query=security%20lock%20icon%20modern%20minimalist%20design%20protection%20symbol%20clean%20simple%20illustration%20abstract%20geometric%20shape%20professional%20tech%20graphic%20light%20neutral%20background&width=300&height=300&seq=feature-security-001&orientation=squarish" alt="Enhanced Security" className="h-full object-contain" />
              </div>
              <h4 className="text-[22px] text-[#1A1A1A] font-semibold mb-4">Enhanced Security</h4>
              <p className="text-[15px] text-[#5A5A5A] leading-[1.7]">
                Our applications implement military-grade encryption and advanced security protocols to protect your data and online activities. We prioritize your digital safety with regular security updates and vulnerability assessments.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="w-[28%] bg-white border border-[#E8E8E8] rounded-2xl p-12">
              <h4 className="text-[20px] text-[#2B2B2B] font-semibold mb-8 text-center">User Privacy</h4>
              <div className="w-full h-[140px] flex items-center justify-center">
                <img src="https://readdy.ai/api/search-image?query=privacy%20shield%20icon%20modern%20minimalist%20design%20protection%20symbol%20clean%20simple%20illustration%20abstract%20geometric%20shape%20professional%20tech%20graphic%20light%20neutral%20background&width=300&height=300&seq=feature-privacy-001&orientation=squarish" alt="User Privacy" className="h-full object-contain" />
              </div>
            </div>
            
            {/* Feature 3 */}
            <div className="w-[28%] bg-white border border-[#E8E8E8] rounded-2xl p-12">
              <h4 className="text-[20px] text-[#2B2B2B] font-semibold mb-8 text-center">Performance</h4>
              <div className="w-full h-[140px] flex items-center justify-center">
                <img src="https://readdy.ai/api/search-image?query=speed%20gauge%20icon%20modern%20minimalist%20design%20performance%20symbol%20clean%20simple%20illustration%20abstract%20geometric%20shape%20professional%20tech%20graphic%20light%20neutral%20background&width=300&height=300&seq=feature-performance-001&orientation=squarish" alt="Performance" className="h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1C1C1C] py-40 px-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="mb-32">
            <h3 className="text-[96px] font-sans font-extrabold text-white leading-none">MOSWINES AS</h3>
          </div>
          
          <div className="flex gap-[5%]">
            {/* Left Column */}
            <div className="w-[30%]">
              <p className="text-base text-[#CCCCCC] leading-[1.7] max-w-[320px] mb-16">
                We are committed to delivering exceptional iOS applications that enhance security and performance. Connect with us to learn more about our products and services.
              </p>
              <div className="flex gap-5">
                <a href="#" className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#1C1C1C] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#1C1C1C] transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#1C1C1C] transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#1C1C1C] transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            {/* Middle Column */}
            <div className="w-[35%]">
              <div className="mb-14">
                <div className="text-[11px] uppercase tracking-[2px] text-white mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-line text-base"></i>
                  <span>ADDRESS</span>
                </div>
                <div className="text-base text-[#CCCCCC] space-y-2">
                  <p>Vendelasen 8</p>
                  <p>NESOYA</p>
                  <p>1397</p>
                  <p>Norway</p>
                </div>
              </div>
              
              <div className="mb-14">
                <div className="text-[11px] uppercase tracking-[2px] text-white mb-3 flex items-center gap-2">
                  <i className="ri-phone-line text-base"></i>
                  <span>PHONE</span>
                </div>
                <p className="text-base text-[#CCCCCC]">+1 2185797390</p>
              </div>
              
              <div>
                <div className="text-[11px] uppercase tracking-[2px] text-white mb-3 flex items-center gap-2">
                  <i className="ri-mail-line text-base"></i>
                  <span>EMAIL</span>
                </div>
                <p className="text-base text-[#CCCCCC]">info@moswinesas.com</p>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="w-[30%]">
              <div className="w-full h-[400px] rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=modern%20office%20environment%20with%20iOS%20devices%20iPhone%20iPad%20on%20desk%20minimalist%20workspace%20clean%20professional%20setting%20natural%20lighting%20contemporary%20interior%20design%20technology%20workspace%20neutral%20tones&width=400&height=600&seq=office-environment-001&orientation=portrait" alt="Office Environment" className="w-full h-full object-cover object-center" />
              </div>
            </div>
          </div>
          
          {/* Bottom Copyright Bar */}
          <div className="mt-12 pt-12 border-t border-[#3A3A3A] flex justify-between items-center">
            <p className="text-[13px] text-[#8A8A8A]">© 2025 Moswines AS</p>
            <div className="flex gap-8">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#8A8A8A] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Website Builder</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
